import {EXAMPLE_VALUES, FORMATS, formatValue} from "./formatting";
import type {
    ContentLanguage,
    PickerVariable,
    ResolveContext,
    ResolveOptions,
    VariablePreview,
} from "./types";
import {DEFAULT_FALLBACK} from "./variableToken";

/** The languages a guest reads a resolved value in. */
export const CONTENT_LANGUAGES: ContentLanguage[] = [
    {code: "de", label: "Deutsch"},
    {code: "en", label: "English"},
    {code: "it", label: "Italiano"},
];

/** The language nobody named. Said out loud, so it no longer hangs on the order of an array. */
export const DEFAULT_CONTENT_LANGUAGE = "de";

export const GUEST_LANGUAGE = "";

/** `Intl` throws a RangeError for anything that is not a three-letter code, which would take the picker down. */
const CURRENCY_CODE = /^[A-Z]{3}$/;

export const valueAtPath = (source: unknown, path: string) =>
    path
        .split(".")
        .reduce<unknown>(
            (value, segment) => (value as Record<string, unknown> | undefined)?.[segment],
            source,
        );

export const guestLanguage = (language: string) => language || DEFAULT_CONTENT_LANGUAGE;

const resolveMeta = (options: ResolveOptions, variable?: PickerVariable) => {
    const context = options.context ?? {};
    const contextLanguage = typeof context.language === "string" ? context.language : "";
    // A catalogue that keeps the code beside the amount says so; otherwise the context carries it.
    const currency = variable?.currency?.(context) ?? context.currency;

    return {
        language: guestLanguage(options.language || contextLanguage),
        currency: typeof currency === "string" && CURRENCY_CODE.test(currency) ? currency : "EUR",
    };
};

// A fresh context per call: a derivation that writes into it cannot leak into the next one.
const resolveRawVariable = (variable: PickerVariable, context: ResolveContext = {}) =>
    variable.resolve ? variable.resolve(context) : valueAtPath(context, variable.path ?? "");

export const resolveVariable = (variable: PickerVariable, options: ResolveOptions = {}) =>
    formatValue(variable.kind, resolveRawVariable(variable, options.context), {
        ...resolveMeta(options, variable),
        format: options.format || FORMATS[variable.kind]?.[0] || "",
    });

/**
 * What an author is shown for a variable: the value in the preview, else the fallback a reader
 * would get. The info card passes no fallback and shows the catalogue's default; the detail panel
 * passes the chip's own, so a cleared default shows as empty rather than as a dash.
 */
export const variableExample = (
    variable: PickerVariable,
    preview: VariablePreview,
    {format, fallback}: {format?: string; fallback?: string} = {},
) => resolveVariable(variable, {language: preview.language, context: preview.context, format})
    || (fallback ?? (variable.fallback || DEFAULT_FALLBACK));

const isMissing = (value: unknown) =>
    value == null || value === "" || (Array.isArray(value) && value.length === 0);

/** Every format of the variable's kind, rendered, so an author picks by reading the result. */
export const formatChoices = (variable: PickerVariable, options: ResolveOptions = {}) => {
    const raw = resolveRawVariable(variable, options.context);
    // An empty value would render every choice blank; the stand-in still shows what each format does.
    const value = isMissing(raw) ? EXAMPLE_VALUES[variable.kind] : raw;

    return (FORMATS[variable.kind] ?? []).map(format => ({
        value: format,
        label: formatValue(variable.kind, value, {...resolveMeta(options, variable), format}),
    }));
};
